import { useCallback, useEffect, useRef, useState } from "react";
import { getApiError } from "./api";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export function useAsync<T>(
  loader: () => Promise<T>,
  dependencies: React.DependencyList = [],
  enabled = true,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const callId = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled) return;
    const id = ++callId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await loader();
      if (id === callId.current) setData(result);
    } catch (reason) {
      if (id === callId.current) setError(getApiError(reason));
    } finally {
      if (id === callId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  useEffect(() => {
    void reload();
    return () => {
      callId.current += 1;
    };
  }, [reload]);

  return { data, loading, error, reload, setData };
}

export function useDebouncedValue<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
